import { useState } from "react";
import { Link } from "react-router-dom";
import { INCIDENTS } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { IncidentCategorySelect } from "@/components/incidents/IncidentCategorySelect";
import { EvidenceUpload } from "@/components/incidents/EvidenceUpload";
import { useIncidentSubmit } from "@/hooks/useIncidentSubmit";

export default function ReportIncident() {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [location, setLocation] = useState("");
  const [incidentDate, setIncidentDate] = useState("");
  const [files, setFiles] = useState([]);
  const [contactPhone, setContactPhone] = useState("");
  const [preferredContactMethod, setPreferredContactMethod] = useState("");
  const [followUpConsent, setFollowUpConsent] = useState(false);
  const { submitIncident, loading, error, submitted, submitLabel } = useIncidentSubmit();

  const handleSubmit = (e) => {
    e.preventDefault();
    submitIncident({ category, title, description, location, incidentDate, files, contactPhone, preferredContactMethod, followUpConsent });
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1E3A6E] mb-2">Document the Behavior</p>
      <h1 className="font-display font-extrabold uppercase text-3xl md:text-4xl text-zinc-950 tracking-tight mb-2">Report an Incident</h1>
      <p className="text-zinc-500 text-sm mb-8">
        This feeds our pattern-tracking data — it's how isolated incidents become documented accountability.
      </p>

      <Link
        to="/guidelines?tab=resources"
        data-testid="report-incident-support-link"
        className="inline-block text-sm font-semibold text-[#1E3A6E] underline hover:text-zinc-950 mb-8"
      >
        Need support right now? See immediate resources &rarr;
      </Link>

      {submitted ? (
        <div data-testid="report-incident-success-message" className="p-6 rounded-sm bg-[#4FD1C5]/10 border border-[#4FD1C5]/40 text-[#1E3A6E] text-center">
          Your report has been submitted and recorded. Thank you for documenting this.
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="bg-white border border-zinc-200 rounded-sm p-6 sm:p-8 space-y-6">
          {error && <div className="p-3 rounded-sm bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

          <IncidentCategorySelect
            value={category} onChange={setCategory}
            testId={INCIDENTS.categorySelect} optionTestId={INCIDENTS.categoryOption}
          />

          <div>
            <Label htmlFor="title" className="text-zinc-500 text-xs uppercase tracking-wide">Title</Label>
            <Input id="title" required value={title} onChange={(e) => setTitle(e.target.value)} data-testid={INCIDENTS.titleInput}
              className="bg-white border-zinc-300 text-zinc-950 focus:border-zinc-950 focus:ring-1 focus:ring-[#4FD1C5] rounded-none mt-1" />
          </div>

          <div>
            <Label htmlFor="description" className="text-zinc-500 text-xs uppercase tracking-wide">What happened?</Label>
            <Textarea id="description" required rows={6} value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="Be as specific as possible — dates, names, what was said or done."
              data-testid={INCIDENTS.descriptionInput}
              className="bg-white border-zinc-300 text-zinc-950 focus:border-zinc-950 focus:ring-1 focus:ring-[#4FD1C5] rounded-none mt-1" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="location" className="text-zinc-500 text-xs uppercase tracking-wide">Location (optional)</Label>
              <Input id="location" value={location} onChange={(e) => setLocation(e.target.value)} data-testid={INCIDENTS.locationInput}
                className="bg-white border-zinc-300 text-zinc-950 focus:border-zinc-950 focus:ring-1 focus:ring-[#4FD1C5] rounded-none mt-1" />
            </div>
            <div>
              <Label htmlFor="incidentDate" className="text-zinc-500 text-xs uppercase tracking-wide">Date (optional)</Label>
              <Input id="incidentDate" type="date" value={incidentDate} onChange={(e) => setIncidentDate(e.target.value)} data-testid={INCIDENTS.dateInput}
                className="bg-white border-zinc-300 text-zinc-950 focus:border-zinc-950 focus:ring-1 focus:ring-[#4FD1C5] rounded-none mt-1" />
            </div>
          </div>

          <EvidenceUpload files={files} onFilesChange={setFiles} testId={INCIDENTS.evidenceUploadInput} />

          <div className="border-t border-zinc-200 pt-6">
            <Label className="text-zinc-500 text-xs uppercase tracking-wide mb-1 block">Follow-Up Contact (optional)</Label>
            <p className="text-zinc-400 text-xs mb-4">
              Leave a number and let us know if it's OK to reach out about this specific report.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <Label htmlFor="contactPhone" className="text-zinc-500 text-xs uppercase tracking-wide">Phone Number</Label>
                <Input id="contactPhone" type="tel" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} data-testid={INCIDENTS.contactPhoneInput}
                  className="bg-white border-zinc-300 text-zinc-950 focus:border-zinc-950 focus:ring-1 focus:ring-[#4FD1C5] rounded-none mt-1" />
              </div>
              <div>
                <Label className="text-zinc-500 text-xs uppercase tracking-wide">Preferred Contact Method</Label>
                <Select value={preferredContactMethod} onValueChange={setPreferredContactMethod}>
                  <SelectTrigger data-testid={INCIDENTS.contactMethodSelect} className="bg-white border-zinc-300 text-zinc-950 rounded-none mt-1 focus:ring-1 focus:ring-[#4FD1C5]">
                    <SelectValue placeholder="Choose one" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-zinc-200 text-zinc-950">
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="phone">Phone</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <label className="flex items-start gap-2 cursor-pointer">
              <Checkbox checked={followUpConsent} onCheckedChange={setFollowUpConsent} data-testid={INCIDENTS.followUpConsentCheckbox} className="mt-0.5" />
              <span className="text-sm text-zinc-600">It's okay for BBAP to follow up with me about this report.</span>
            </label>
          </div>

          <Button type="submit" disabled={loading} data-testid={INCIDENTS.submitButton} className="w-full bg-zinc-950 text-white font-medium rounded-none hover:bg-zinc-900 hover:shadow-[0px_4px_0px_0px_#4FD1C5]">
            {submitLabel}
          </Button>
        </form>
      )}
    </div>
  );
}
